import { Pin, PinOff, Bell, BellOff, Pencil, LogOut } from 'lucide-react';
import type { Channel } from '../types';
import type { ContextMenuItem } from './ContextMenu';

export interface ChannelMenuHandlers {
  onTogglePin?: (channel: Channel) => void;
  onToggleMute?: (channel: Channel) => void;
  /** 그룹 대화방 이름 변경 — 입력 모달은 App이 담당 */
  onRenameChannel?: (channel: Channel) => void;
  /** 채팅방 나가기 — 확인 모달은 App이 담당 */
  onLeaveChannel?: (channel: Channel) => void;
}

/**
 * 대화방 관리 메뉴의 공통 꼬리(고정 / 알림 / 이름 변경 / 나가기).
 * 대화방 목록(ChatListScreen)·홈 최근 대화(HomeScreen)·모바일 채팅 헤더 햄버거(App)
 * 세 곳이 같은 순서·라벨을 쓰도록 한 곳에서 조립한다 — 예전엔 세 곳에 복제돼 있어
 * 항목을 하나 늘릴 때마다 전부 따로 고쳐야 했다.
 *
 * - 나와의 채팅(self)과 초안 DM(id 0, 서버에 없음)은 관리 메뉴가 없다 → 빈 배열
 * - 이름 변경은 group만: dm/self는 이름이 없고 public은 관리자 콘솔 소관
 * - 나가기는 group/dm만: public은 나갈 수 없다. 파괴적 항목은 항상 맨 끝
 * - onSelect는 메뉴를 먼저 닫고 핸들러를 부른다(헤더 메뉴의 기존 순서)
 */
export function buildChannelMenuItems(
  channel: Channel,
  handlers: ChannelMenuHandlers,
  close: () => void,
): ContextMenuItem[] {
  if (channel.type === 'self' || !channel.id) return [];
  const run = (fn?: (c: Channel) => void) => () => {
    close();
    fn?.(channel);
  };
  const isGroup = channel.type === 'group';
  const isDm = channel.type === 'dm';
  return [
    {
      key: 'pin',
      label: channel.isPinned ? '고정 해제' : '채팅방 고정',
      icon: channel.isPinned ? PinOff : Pin,
      onSelect: run(handlers.onTogglePin),
    },
    {
      key: 'mute',
      label: channel.isMuted ? '알림 켜기' : '알림 끄기',
      icon: channel.isMuted ? Bell : BellOff,
      onSelect: run(handlers.onToggleMute),
    },
    ...(handlers.onRenameChannel && isGroup
      ? [{ key: 'rename', label: '대화방 이름 변경', icon: Pencil, onSelect: run(handlers.onRenameChannel) }]
      : []),
    ...(handlers.onLeaveChannel && (isGroup || isDm)
      ? [{ key: 'leave', label: '채팅방 나가기', icon: LogOut, danger: true, onSelect: run(handlers.onLeaveChannel) }]
      : []),
  ];
}
